// ─────────────────────────────────────────────────────────────────
// Glass-i — Delivery Window helpers (Stage DISPATCH-WINDOW-1). Permission key,
// window presets, readiness/status presentation, and area/weight/date
// formatting. Pure — no side effects.
// ─────────────────────────────────────────────────────────────────

import dayjs from 'dayjs';

export const PERM = Object.freeze({ VIEW: 'erp.dispatch.window.view' });

export function hasPerm(me, key) {
  const list = (me && me.permissions) || [];
  return list.includes('*') || list.includes(key);
}

// CLAUDE.md rule 5A — glass area (SQM/SQFT) is ALWAYS shown at exactly 3 dp with
// Indian grouping. Display-only; the stored/actual values keep full precision.
const AREA_NF3 = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
export function formatArea(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? AREA_NF3.format(n) : '—';
}

// Weight — 1 dp, Indian grouping. null/undefined → em dash (mass not determinable).
const WEIGHT_NF1 = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
export function formatWeight(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? WEIGHT_NF1.format(n) : '—';
}

// Counts (pcs / WOs) — plain Indian grouping, no decimals.
const INT_NF = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
export function formatInt(v) {
  const n = Number(v);
  return Number.isFinite(n) ? INT_NF.format(n) : '0';
}

// "12.00" → "12", "12.76" → "12.76". Handles the comma-joined `thicks` string too.
export function formatThickness(mm) {
  if (mm === null || mm === undefined || mm === '') return '';
  return String(mm)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => String(Number(s)).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1'))
    .join(', ');
}

export function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  return `${day}-${d.toLocaleString('en-US', { month: 'short' })}-${d.getFullYear()}`;
}
// Compact "28 Jul" for timeline / group headers.
export function fmtDay(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getDate()).padStart(2, '0')} ${d.toLocaleString('en-US', { month: 'short' })}`;
}
export function dow(v) {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
}
export function fmtWindow(from, to) {
  if (from && to && from === to) return fmtDate(from);
  return `${fmtDate(from)} → ${fmtDate(to)}`;
}

// Readiness status → { label, color } for an AntD Tag (mirrors dispatchPlanHelpers).
const READINESS = {
  READY: { label: 'Ready', color: 'green' },
  PARTIAL: { label: 'Partially ready', color: 'gold' },
  PENDING: { label: 'In production', color: 'blue' },
  NO_PIECES: { label: 'No pieces', color: 'default' }
};
export function readinessTag(status) { return READINESS[status] || READINESS.NO_PIECES; }

// WO production status → { label, color }.
const WO_STATUS = {
  RELEASED: { label: 'Released', color: 'blue' },
  IN_PRODUCTION: { label: 'In production', color: 'gold' },
  COMPLETED: { label: 'Completed', color: 'green' },
  CANCELLED: { label: 'Cancelled', color: 'default' }
};
export function woStatusTag(status) { return WO_STATUS[status] || { label: status || '—', color: 'default' }; }

// Delivery-window presets → [fromDayjs, toDayjs]. Monday-based weeks.
export const PRESET_ORDER = ['today', 'week', 'next', 'fortnight', 'month', 'all'];
export const PRESET_LABELS = {
  today: 'Today', week: 'This week', next: 'Next week', fortnight: 'Next 14 days', month: 'This month', all: 'All upcoming'
};
export function presetRange(key) {
  const today = dayjs().startOf('day');
  const startOfWeek = today.day() === 0 ? today.subtract(6, 'day') : today.startOf('week').add(1, 'day'); // Monday
  switch (key) {
    case 'today': return [today, today];
    case 'week': return [startOfWeek, startOfWeek.add(6, 'day')];
    case 'next': return [startOfWeek.add(7, 'day'), startOfWeek.add(13, 'day')];
    case 'fortnight': return [today, today.add(13, 'day')];
    case 'month': return [today.startOf('month'), today.endOf('month')];
    case 'all': return [today.subtract(30, 'day'), today.add(365, 'day')];
    default: return [today, today.add(13, 'day')];
  }
}

// A piece is jumbo if a side ≥ 2400 mm or the pane ≥ 4 m² (needs A-frame handling).
export function isJumboSize(widthMm, heightMm) {
  const w = Number(widthMm), h = Number(heightMm);
  if (!(w > 0) || !(h > 0)) return false;
  return Math.max(w, h) >= 2400 || (w * h) / 1e6 >= 4.0;
}
